import { useState, useEffect, useCallback } from 'react';
import { isEmpty } from 'lodash-es';
import { EventToolbox } from 'client/utils/event-toolbox';
import { venomHistory } from 'client/utils/history/venom-history';
import { getCreateListingUrl } from 'site-modules/shared/utils/private-party/private-party';
import { redactLicensePlateString } from 'site-modules/shared/components/appraisal/appraisal-tabs/license-plate-tab/license-plate-tab-utils';
import { getSquishVinNoMmyPixelValue } from 'site-modules/shared/utils/appraisal/tracking';
import { delayHeavyTask } from 'client/site-modules/shared/utils/inp-utils';
import { TrackingConstant } from 'client/tracking/constant';
import { PRIVATE_PARTY_EVENTS } from 'site-modules/shared/constants/private-party/private-party-events';
import { LICENSE_PLATE_TAB } from 'site-modules/shared/components/appraisal/appraisal-tabs-container/appraisal-tab-constants';
import {
  useAppraisalTabsConfig,
  useAppraisalTabsState,
} from 'site-modules/shared/components/appraisal/appraisal-tabs-v2/context/appraisal-tabs-context';
import { usePrevious } from 'site-modules/shared/hooks/use-previous';
import { ERROR_MESSAGE_MAP } from 'site-modules/shared/components/appraisal/appraisal-tabs-v2/hooks/use-license-plate-validation';
import { useLicensePlateTracking } from 'site-modules/shared/components/appraisal/appraisal-tabs-v2/hooks/use-license-plate-tracking';
import { LP_ERROR_KEYS } from 'site-modules/shared/components/appraisal/appraisal-tabs-v2/constants/license-plate-constants';

export function useLicensePlateSubmit({
  validateFields,
  setLpError,
  isLpValid,
  vinFromLicensePlate,
  vinDecodeResponse,
  licensePlateLookupError,
  vinTabLink,
  onCompleteCallback,
  decodeAndSetStyles,
  lookupVin,
  isAppraisalValuePage,
  vinEligibility,
  isLicensePlateAndVinStep,
  licensePlateInputValue,
  stateCodeInputValue,
  clearVin,
  clearDecodedVinEligibility,
  isPrivatePartyBtn,
  isWIDG1125chal1,
  isSellInCityState,
}) {
  const { creativeId } = useAppraisalTabsConfig();
  const { fireTrackAction, fireLicensePlateLookupTracking, fireVinDecodeError } = useLicensePlateTracking(creativeId);
  const {
    vin: vinFromContext,
    setVin,
    setMmy,
    setStyleIds,
    setIsReadyForRedirect,
    isAppraiserPageOpened,
    licensePlate,
    stateCode,
    styleIds,
    mmy,
  } = useAppraisalTabsState();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [wasFormSubmitted, setWasFormSubmitted] = useState(false);
  const [isMultipleVinProcessed, setIsMultipleVinProcessed] = useState(false);
  const [isMultipleVinsModalOpen, setIsMultipleVinsModalOpen] = useState(false);

  const vinValueFromLp = vinFromLicensePlate?.vin;
  const vinLength = vinFromLicensePlate?.vins?.length;
  const isMultiVins = (vinLength ?? 0) > 1;

  const prevIsAppraiserPageOpened = usePrevious(isAppraiserPageOpened);

  const resetIsSubmitting = useCallback(() => {
    setIsSubmitting(false);
  }, []);

  const closeMultipleVinsModal = useCallback(() => {
    setIsMultipleVinsModalOpen(false);
  }, []);

  // pageshow listener for bfcache reset
  useEffect(() => {
    const handlePageShow = ({ persisted }) => {
      if (persisted) {
        resetIsSubmitting();
        setWasFormSubmitted(false);
      }
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => {
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [resetIsSubmitting]);

  // Reset submitting when /appraisal-value/ page is opened
  useEffect(() => {
    if (isAppraiserPageOpened && !prevIsAppraiserPageOpened) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- reset local submit state after external navigation state changes.
      resetIsSubmitting();
    }
  }, [isAppraiserPageOpened, prevIsAppraiserPageOpened, resetIsSubmitting]);

  // Don't block the CTA for multi-VINs; open modal when multi-VIN
  useEffect(() => {
    if (isMultiVins && isSubmitting) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- consume the external multi-VIN lookup response.
      resetIsSubmitting();

      if (!isMultipleVinProcessed && !isMultipleVinsModalOpen) {
        setIsMultipleVinsModalOpen(true);
      }
    }
  }, [isMultiVins, isSubmitting, isMultipleVinProcessed, isMultipleVinsModalOpen, resetIsSubmitting]);

  const isReadyForUpdate =
    wasFormSubmitted &&
    (isMultiVins ? isMultipleVinProcessed : isSubmitting) &&
    !!vinFromLicensePlate &&
    licensePlateInputValue === licensePlate &&
    stateCodeInputValue === stateCode &&
    (isAppraisalValuePage && vinValueFromLp ? !isEmpty(vinEligibility) || isLicensePlateAndVinStep : true);

  const prevIsReadyForUpdate = usePrevious(isReadyForUpdate);

  // Happy path / error path when LP lookup result is ready.
  // The effect intentionally depends only on `isReadyForUpdate`.
  useEffect(() => {
    if (!isReadyForUpdate || prevIsReadyForUpdate) return;

    if (isMultiVins && isMultipleVinProcessed && !vinFromContext && !licensePlateLookupError) {
      fireLicensePlateLookupTracking(TrackingConstant.VIN_FOUND);
      // eslint-disable-next-line react-hooks/set-state-in-effect -- consume the external multi-VIN selection state.
      setIsMultipleVinProcessed(false);
      return;
    }

    if (!isMultiVins && vinValueFromLp && !licensePlateLookupError) {
      fireLicensePlateLookupTracking(TrackingConstant.VIN_FOUND);

      if (!isAppraisalValuePage) {
        decodeAndSetStyles(vinValueFromLp);
      }

      if (isWIDG1125chal1 && isSellInCityState) {
        venomHistory.push(`/sell-car/instant-cash-offer/?icoVin=${vinValueFromLp}`);
        return;
      }

      if (isPrivatePartyBtn) {
        EventToolbox.fireCustomEvent(PRIVATE_PARTY_EVENTS.START_PP_LISTING, {
          value: LICENSE_PLATE_TAB.label,
          creativeId,
        });
        venomHistory.push(getCreateListingUrl(vinValueFromLp));
        return;
      }

      onCompleteCallback(vinValueFromLp, vinEligibility);
      resetIsSubmitting();
      return;
    }

    if (isMultiVins && vinFromContext && !licensePlateLookupError) {
      onCompleteCallback(vinFromContext, vinEligibility);
      setIsMultipleVinProcessed(false);
      return;
    }

    // Error cases
    if (vinValueFromLp === null || licensePlateLookupError) {
      setLpError(LP_ERROR_KEYS.LICENSE_PLATE, ERROR_MESSAGE_MAP.licensePlate.ERROR);
    } else {
      setLpError(LP_ERROR_KEYS.LICENSE_PLATE_WIDE, ERROR_MESSAGE_MAP.licensePlate.LANDING_INVALID(vinTabLink));
    }

    fireLicensePlateLookupTracking(TrackingConstant.VIN_NOT_FOUND);
    setIsSubmitting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReadyForUpdate]);

  // VIN decode ready path
  const vinDecodeStyles = vinDecodeResponse?.results;
  const hasVinDecodeStyles = !!vinDecodeStyles && vinDecodeStyles.length > 0;
  const isVinDecodeReadyForUpdate =
    !!vinDecodeStyles && wasFormSubmitted && isLpValid && !isSubmitting && (!isMultiVins || isMultipleVinProcessed);
  const prevIsVinDecodeReadyForUpdate = usePrevious(isVinDecodeReadyForUpdate);

  useEffect(() => {
    if (!isVinDecodeReadyForUpdate || prevIsVinDecodeReadyForUpdate) return;

    if (hasVinDecodeStyles) {
      const { makeNiceId, modelNiceId, year } = vinDecodeStyles[0];
      setMmy({ make: makeNiceId, model: modelNiceId, year });
    } else {
      fireVinDecodeError(getSquishVinNoMmyPixelValue(vinFromContext, vinDecodeResponse));
      setLpError(
        LP_ERROR_KEYS.LICENSE_PLATE_WIDE,
        isAppraisalValuePage
          ? ERROR_MESSAGE_MAP.licensePlate.NO_STYLES
          : ERROR_MESSAGE_MAP.licensePlate.LANDING_INVALID(vinTabLink)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVinDecodeReadyForUpdate]);

  const areStyleIdsLoaded = !!styleIds;

  useEffect(() => {
    if (isVinDecodeReadyForUpdate && !isEmpty(mmy) && areStyleIdsLoaded) {
      delayHeavyTask(() => {
        setIsReadyForRedirect(true);
      });
    }
  }, [isVinDecodeReadyForUpdate, mmy, setIsReadyForRedirect, areStyleIdsLoaded]);

  // Reset processed state when LP/state input values change.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- reset workflow state when controlled form inputs change.
    setWasFormSubmitted(false);
    setIsMultipleVinProcessed(false);
  }, [licensePlateInputValue, stateCodeInputValue]);

  const onSubmit = useCallback(
    e => {
      e.preventDefault();

      delayHeavyTask(async () => {
        setMmy({});
        setStyleIds(null);
        await clearVin();
        setVin('');
        clearDecodedVinEligibility();

        const { isValid } = validateFields(licensePlateInputValue, stateCodeInputValue);

        setWasFormSubmitted(true);
        setIsMultipleVinProcessed(false);

        if (!isValid) {
          return;
        }

        setIsSubmitting(true);
        lookupVin(licensePlateInputValue, stateCodeInputValue);

        fireTrackAction(
          TrackingConstant.EVENT_TYPE_ACTION_PROGRESS,
          TrackingConstant.SUBMIT_VEHICLE,
          TrackingConstant.LICENSE_PLATE_SPACE
        );
        fireTrackAction(
          TrackingConstant.EVENT_TYPE_ACTION_START,
          TrackingConstant.LICENSE_PLATE,
          redactLicensePlateString(licensePlateInputValue)
        );
        fireTrackAction(
          TrackingConstant.EVENT_TYPE_ACTION_START,
          TrackingConstant.LICENSE_PLATE_STATE,
          stateCodeInputValue
        );
      });
    },
    [
      clearDecodedVinEligibility,
      clearVin,
      fireTrackAction,
      licensePlateInputValue,
      lookupVin,
      setMmy,
      setStyleIds,
      setVin,
      stateCodeInputValue,
      validateFields,
    ]
  );

  return {
    isSubmitting,
    wasFormSubmitted,
    isMultipleVinProcessed,
    isMultipleVinsModalOpen,
    setIsMultipleVinProcessed,
    closeMultipleVinsModal,
    resetIsSubmitting,
    onSubmit,
  };
}
